import React, { useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Hero } from "@/components/sections/Hero";
import { CTABanner } from "@/components/sections/CTABanner";
import { Reveal } from "@/components/ui/Reveal";
import { SectionHeading } from "@/components/ui/RichText";
import { ServiceCard } from "@/components/sections/ServiceCard";
import { Pagination } from "@/components/ui/Paginator";
import { Seo } from "@/components/ui/Seo";
import { ErrorBanner, EmptyState, CardSkeleton } from "@/components/ui/States";
import { useFetch } from "@/hooks/useFetch";
import { localizedPath, pick } from "@/lib/helpers";
import { mediaUrl } from "@/lib/api";

export default function Services() {
  const { lang = "en" } = useParams();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));
  const listRef = useRef(null);

  const { data: pageData, error: pe, refetch: refetchPage } = useFetch(`/pages/services`, []);
  const { data: services, loading: sl, error: se, refetch: refetchSvc } = useFetch(`/services?page=${page}&perPage=9`, [page]);

  // NEVER gate the whole page on a single failed fetch; hero stays visible.

  const items = services?.items || [];
  const pages = services?.pages || 1;
  // Fallbacks while /api/pages/services is in-flight: keep the hero shell visible
  const heroTitle = pick(pageData?.heroTitle, lang) || t("nav.services");
  const heroSubtitle = pick(pageData?.heroSubtitle, lang);
  const heroEyebrow = pick(pageData?.heroEyebrow, lang);
  const heroImage = pageData?.heroImageUrl;

  const changePage = (p) => {
    setParams({ page: String(p) });
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  return (
    <>
      <Seo title={pick(pageData?.seo?.metaTitle, lang)} description={pick(pageData?.seo?.metaDescription, lang)} lang={lang} path={localizedPath(lang, "/services")} image={mediaUrl(heroImage)} />

      <Hero eyebrow={heroEyebrow} title={heroTitle} subtitle={heroSubtitle} image={heroImage}>
        <Link to={localizedPath(lang, "/contact")} className="btn-primary" data-testid="services-hero-cta">
          {t("cta.talkExpert")} <ArrowRight className="w-4 h-4" />
        </Link>
      </Hero>

      {pe && !pageData ? <ErrorBanner onRetry={refetchPage} testId="services-page-error" /> : null}
      {se && !services ? <ErrorBanner onRetry={refetchSvc} testId="services-list-error" /> : null}

      <section className="container-x py-20 md:py-28">
        <div ref={listRef} className="scroll-mt-24">
          <SectionHeading eyebrow={t("services.listingTitle")} title={t("nav.services")} />
        </div>

        {sl ? (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="services-skeleton">
            {[0, 1, 2, 3, 4, 5].map((i) => <CardSkeleton key={i} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-12"><EmptyState message={t("services.empty")} /></div>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.08}>
                <ServiceCard service={s} />
              </Reveal>
            ))}
          </div>
        )}

        <Pagination page={services?.page || page} pages={pages} onChange={changePage} testIdPrefix="services-pagination" />
      </section>

      {pageData?.process?.length ? (
        <section className="bg-surface border-y border-hair py-20 md:py-28" data-testid="services-process">
          <div className="container-x">
            <SectionHeading title={pick(pageData.processTitle, lang)} />
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {pageData.process.map((p, i) => (
                <Reveal key={p.step ?? `step-${i}`} delay={i * 0.08} className="glass-card p-7">
                  <div className="font-display font-bold text-accent text-5xl leading-none">{p.step}</div>
                  <h3 className="mt-5 font-display font-bold uppercase text-fg text-lg tracking-wide">{pick(p.title, lang)}</h3>
                  <p className="mt-3 text-mut text-sm leading-relaxed">{pick(p.body, lang)}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {pageData ? <CTABanner title={pick(pageData.heroTitle, lang)} subtitle={pick(pageData.heroSubtitle, lang)} image={pageData.heroImageUrl} /> : null}
    </>
  );
}
